import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, Plus, Search, Edit, Trash2, Upload, X, Sparkles, Link2 } from 'lucide-react';
import API_BASE_URL from '../config/api';

const ROLES = ['gp', 'referrer', 'radiologist', 'acc', 'physio', 'other'];

const ROLE_LABELS = {
  gp: 'GP',
  referrer: 'Referrer',
  radiologist: 'Radiologist',
  acc: 'ACC',
  physio: 'Physio',
  other: 'Other'
};

const ROLE_COLORS = {
  gp: { bg: '#dbeafe', color: '#1e40af' },
  referrer: { bg: '#fef3c7', color: '#92400e' },
  radiologist: { bg: '#ede9fe', color: '#5b21b6' },
  acc: { bg: '#d1fae5', color: '#065f46' },
  physio: { bg: '#fce7f3', color: '#9d174d' },
  other: { bg: '#e5e7eb', color: '#374151' }
};

const emptyForm = {
  name: '',
  practiceName: '',
  role: 'gp',
  email: '',
  phone: '',
  fax: '',
  address: '',
  notes: ''
};

function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [csvPreview, setCsvPreview] = useState([]);
  const [importing, setImporting] = useState(false);

  // Referrer-directory review gate (Phase 1)
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [suggestRoles, setSuggestRoles] = useState({}); // nameKey -> role
  const [suggestLinkTo, setSuggestLinkTo] = useState({}); // nameKey -> contactId
  const [busyKey, setBusyKey] = useState(null); // nameKey currently being actioned

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (roleFilter) params.role = roleFilter;
      if (searchQuery) params.search = searchQuery;
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/contacts`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data);
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setLoading(false);
    }
  }, [roleFilter, searchQuery]);

  const fetchSuggestions = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/contacts/referrer-suggestions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(response.data || []);
    } catch (err) {
      console.error('Error fetching referrer suggestions:', err);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const formatNZ = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return isNaN(dt) ? '' : dt.toLocaleDateString('en-NZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('token')}` });

  const handleAddSuggestion = async (s) => {
    setBusyKey(s.nameKey);
    try {
      await axios.post(
        `${API_BASE_URL}/contacts/referrer-suggestions/add`,
        { nameKey: s.nameKey, name: s.displayName, practiceName: s.clinic, role: suggestRoles[s.nameKey] || 'referrer' },
        { headers: authHeaders() }
      );
      await Promise.all([fetchSuggestions(), fetchContacts()]);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add contact');
    } finally {
      setBusyKey(null);
    }
  };

  const handleLinkSuggestion = async (s) => {
    const contactId = suggestLinkTo[s.nameKey] || s.possibleMatches?.[0]?.id;
    if (!contactId) return;
    setBusyKey(s.nameKey);
    try {
      await axios.post(
        `${API_BASE_URL}/contacts/referrer-suggestions/link`,
        { nameKey: s.nameKey, contactId, practiceName: s.clinic },
        { headers: authHeaders() }
      );
      await Promise.all([fetchSuggestions(), fetchContacts()]);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to link contact');
    } finally {
      setBusyKey(null);
    }
  };

  const handleDismissSuggestion = async (s) => {
    setBusyKey(s.nameKey);
    try {
      await axios.post(
        `${API_BASE_URL}/contacts/referrer-suggestions/dismiss`,
        { nameKey: s.nameKey, name: s.displayName },
        { headers: authHeaders() }
      );
      await fetchSuggestions();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to dismiss suggestion');
    } finally {
      setBusyKey(null);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleRoleFilterChange = (e) => {
    setRoleFilter(e.target.value);
  };

  const openAddModal = () => {
    setEditingContact(null);
    setForm({ ...emptyForm });
    setError('');
    setShowModal(true);
  };

  const openEditModal = (contact) => {
    setEditingContact(contact);
    setForm({
      name: contact.name || '',
      practiceName: contact.practiceName || '',
      role: contact.role || 'gp',
      email: contact.email || '',
      phone: contact.phone || '',
      fax: contact.fax || '',
      address: contact.address || '',
      notes: contact.notes || ''
    });
    setError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingContact(null);
    setForm({ ...emptyForm });
    setError('');
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const token = sessionStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (editingContact) {
        await axios.put(`${API_BASE_URL}/contacts/${editingContact.id}`, form, { headers });
      } else {
        await axios.post(`${API_BASE_URL}/contacts`, form, { headers });
      }
      closeModal();
      fetchContacts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (contact) => {
    if (!window.confirm(`Are you sure you want to delete ${contact.name}?`)) {
      return;
    }
    try {
      const token = sessionStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/contacts/${contact.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchContacts();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete contact');
    }
  };

  const parseCsv = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map((v) => v.trim());
      if (values.length < 2) continue;
      const row = {};
      headers.forEach((h, idx) => {
        row[h] = values[idx] || '';
      });
      rows.push(row);
    }
    return rows;
  };

  const handleCsvTextChange = (text) => {
    setCsvText(text);
    if (text.trim()) {
      setCsvPreview(parseCsv(text));
    } else {
      setCsvPreview([]);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      setCsvText(text);
      if (text.trim()) {
        setCsvPreview(parseCsv(text));
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!csvText.trim()) return;
    setImporting(true);
    try {
      const token = sessionStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/contacts/import-csv`,
        { csvData: csvText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowImportModal(false);
      setCsvText('');
      setCsvPreview([]);
      fetchContacts();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to import CSV');
    } finally {
      setImporting(false);
    }
  };

  const getRoleBadge = (role) => {
    const colors = ROLE_COLORS[role] || ROLE_COLORS.other;
    const label = ROLE_LABELS[role] || role;
    return (
      <span style={{
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: '9999px',
        fontSize: '12px',
        fontWeight: '500',
        backgroundColor: colors.bg,
        color: colors.color
      }}>
        {label}
      </span>
    );
  };

  if (loading && contacts.length === 0) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb' }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #2563eb',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Header */}
      <header style={{ backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '16px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Users style={{ width: '28px', height: '28px', color: '#2563eb' }} />
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', margin: 0 }}>Contacts</h1>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: '1280px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Controls Row */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          marginBottom: '24px',
          alignItems: 'center'
        }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: '1 1 240px', minWidth: '200px' }}>
            <Search style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '18px',
              height: '18px',
              color: '#9ca3af'
            }} />
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                border: '2px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                boxSizing: 'border-box',
                outline: 'none'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#2563eb'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
            />
          </div>

          {/* Role Filter */}
          <select
            value={roleFilter}
            onChange={handleRoleFilterChange}
            style={{
              padding: '10px 12px',
              border: '2px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer',
              minWidth: '140px'
            }}
          >
            <option value="">All Roles</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>{ROLE_LABELS[r]}</option>
            ))}
          </select>

          {/* Add Contact */}
          <button
            onClick={openAddModal}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '10px 20px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1d4ed8'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563eb'}
          >
            <Plus style={{ width: '18px', height: '18px', marginRight: '6px' }} />
            Add Contact
          </button>

          {/* Import CSV */}
          <button
            onClick={() => { setShowImportModal(true); setCsvText(''); setCsvPreview([]); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '10px 20px',
              backgroundColor: 'white',
              color: '#374151',
              border: '2px solid #d1d5db',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; e.currentTarget.style.borderColor = '#9ca3af'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.borderColor = '#d1d5db'; }}
          >
            <Upload style={{ width: '18px', height: '18px', marginRight: '6px' }} />
            Import CSV
          </button>
        </div>

        {/* Referrer suggestions — review gate */}
        {suggestions.length > 0 && (
          <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', marginBottom: '24px', overflow: 'hidden' }}>
            <div
              onClick={() => setShowSuggestions((v) => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '16px 24px', cursor: 'pointer', borderBottom: showSuggestions ? '1px solid #fde68a' : 'none' }}
            >
              <Sparkles style={{ width: '20px', height: '20px', color: '#b45309' }} />
              <h2 style={{ fontSize: '16px', fontWeight: '600', margin: 0, color: '#92400e' }}>
                Referrer suggestions ({suggestions.length})
              </h2>
              <span style={{ fontSize: '13px', color: '#b45309', marginLeft: 'auto' }}>
                New referrers seen on referrals — {showSuggestions ? 'hide' : 'show'}
              </span>
            </div>
            {showSuggestions && (
              <div>
                {suggestions.map((s) => {
                  const busy = busyKey === s.nameKey;
                  return (
                    <div key={s.nameKey} style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', padding: '14px 24px', borderTop: '1px solid #fef3c7' }}>
                      {/* Identity */}
                      <div style={{ flex: '1 1 280px', minWidth: '220px' }}>
                        <div style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>{s.displayName}</div>
                        <div style={{ fontSize: '13px', color: '#4b5563' }}>
                          {s.clinic ? s.clinic : <span style={{ color: '#9ca3af' }}>No clinic captured</span>}
                        </div>
                        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                          Seen {s.occurrences}× · last {formatNZ(s.lastSeen)}{s.samplePatient ? ` · e.g. ${s.samplePatient}` : ''}
                        </div>
                      </div>
                      {/* Actions */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                        <select
                          value={suggestRoles[s.nameKey] || 'referrer'}
                          onChange={(e) => setSuggestRoles((p) => ({ ...p, [s.nameKey]: e.target.value }))}
                          disabled={busy}
                          style={{ padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', backgroundColor: 'white', cursor: 'pointer' }}
                        >
                          {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                        </select>
                        <button
                          onClick={() => handleAddSuggestion(s)}
                          disabled={busy}
                          style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '7px 12px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: busy ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '500' }}
                        >
                          <Plus style={{ width: '14px', height: '14px' }} /> Add
                        </button>
                        {s.possibleMatches && s.possibleMatches.length > 0 && (
                          <>
                            <select
                              value={suggestLinkTo[s.nameKey] || s.possibleMatches[0].id}
                              onChange={(e) => setSuggestLinkTo((p) => ({ ...p, [s.nameKey]: e.target.value }))}
                              disabled={busy}
                              style={{ padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', backgroundColor: 'white', cursor: 'pointer', maxWidth: '200px' }}
                            >
                              {s.possibleMatches.map((m) => (
                                <option key={m.id} value={m.id}>{m.name}{m.practiceName ? ` — ${m.practiceName}` : ''}</option>
                              ))}
                            </select>
                            <button
                              onClick={() => handleLinkSuggestion(s)}
                              disabled={busy}
                              title="Link to this existing contact and refresh their clinic"
                              style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '7px 12px', backgroundColor: '#ede9fe', color: '#5b21b6', border: 'none', borderRadius: '6px', cursor: busy ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '500' }}
                            >
                              <Link2 style={{ width: '14px', height: '14px' }} /> Link
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDismissSuggestion(s)}
                          disabled={busy}
                          title="Not a referrer to track — hide this"
                          style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '7px 12px', backgroundColor: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '6px', cursor: busy ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '500' }}
                        >
                          <X style={{ width: '14px', height: '14px' }} /> Dismiss
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Table Card */}
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#111827' }}>
              All Contacts ({contacts.length})
            </h2>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f9fafb' }}>
                <tr>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Name</th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Practice</th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Role</th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Email</th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Phone</th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: '48px 24px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                      {searchQuery || roleFilter ? 'No contacts match your filters.' : 'No contacts yet. Add one to get started.'}
                    </td>
                  </tr>
                ) : (
                  contacts.map((contact, index) => (
                    <tr key={contact.id} style={{ borderTop: index > 0 ? '1px solid #e5e7eb' : 'none' }}>
                      <td style={{ padding: '16px 24px', fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                        {contact.name}
                      </td>
                      <td style={{ padding: '16px 24px', fontSize: '14px', color: '#4b5563' }}>
                        {contact.practiceName || '-'}
                      </td>
                      <td style={{ padding: '16px 24px' }}>
                        {getRoleBadge(contact.role)}
                      </td>
                      <td style={{ padding: '16px 24px', fontSize: '14px', color: '#4b5563' }}>
                        {contact.email || '-'}
                      </td>
                      <td style={{ padding: '16px 24px', fontSize: '14px', color: '#4b5563' }}>
                        {contact.phone || '-'}
                      </td>
                      <td style={{ padding: '16px 24px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => openEditModal(contact)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '6px 12px',
                              backgroundColor: '#dbeafe',
                              color: '#1e40af',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: '500'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#bfdbfe'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dbeafe'}
                          >
                            <Edit style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(contact)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '6px 12px',
                              backgroundColor: '#fee2e2',
                              color: '#dc2626',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: '500'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fecaca'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
                          >
                            <Trash2 style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '560px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, color: '#111827' }}>
                {editingContact ? 'Edit Contact' : 'Add Contact'}
              </h2>
              <button
                onClick={closeModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#111827'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
              >
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            {error && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px'
              }}>
                <p style={{ color: '#dc2626', fontSize: '14px', margin: 0 }}>{error}</p>
              </div>
            )}

            <form onSubmit={handleSave}>
              {/* Name */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Name *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  placeholder="Dr John Smith"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Practice Name */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Practice Name
                </label>
                <input
                  type="text"
                  value={form.practiceName}
                  onChange={(e) => handleFormChange('practiceName', e.target.value)}
                  placeholder="City Medical Centre"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Role */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Role *
                </label>
                <select
                  value={form.role}
                  onChange={(e) => handleFormChange('role', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    boxSizing: 'border-box'
                  }}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                  ))}
                </select>
              </div>

              {/* Email & Phone row */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => handleFormChange('email', e.target.value)}
                    placeholder="john@practice.co.nz"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                    Phone
                  </label>
                  <input
                    type="text"
                    value={form.phone}
                    onChange={(e) => handleFormChange('phone', e.target.value)}
                    placeholder="09 123 4567"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              {/* Fax */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Fax
                </label>
                <input
                  type="text"
                  value={form.fax}
                  onChange={(e) => handleFormChange('fax', e.target.value)}
                  placeholder="09 123 4568"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Address */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Address
                </label>
                <input
                  type="text"
                  value={form.address}
                  onChange={(e) => handleFormChange('address', e.target.value)}
                  placeholder="123 Queen Street, Auckland"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Notes */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                  Notes
                </label>
                <textarea
                  value={form.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  placeholder="Additional notes..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: '10px 20px',
                    backgroundColor: saving ? '#93c5fd' : '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                  onMouseEnter={(e) => { if (!saving) e.currentTarget.style.backgroundColor = '#1d4ed8'; }}
                  onMouseLeave={(e) => { if (!saving) e.currentTarget.style.backgroundColor = '#2563eb'; }}
                >
                  {saving ? 'Saving...' : (editingContact ? 'Save Changes' : 'Add Contact')}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  style={{
                    flex: 1,
                    padding: '10px 20px',
                    backgroundColor: '#e5e7eb',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d1d5db'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showImportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '700px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, color: '#111827' }}>
                Import Contacts from CSV
              </h2>
              <button
                onClick={() => { setShowImportModal(false); setCsvText(''); setCsvPreview([]); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#111827'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
              >
                <X style={{ width: '24px', height: '24px' }} />
              </button>
            </div>

            {/* File upload */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Upload CSV File
              </label>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileUpload}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px dashed #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                  backgroundColor: '#f9fafb'
                }}
              />
            </div>

            {/* Or paste */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Or Paste CSV Data
              </label>
              <textarea
                value={csvText}
                onChange={(e) => handleCsvTextChange(e.target.value)}
                placeholder={"name,practiceName,role,email,phone,fax,address,notes\nDr Smith,City Medical,gp,smith@med.co.nz,09 123 4567,,123 Queen St,"}
                rows={6}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontFamily: 'monospace',
                  boxSizing: 'border-box',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Preview */}
            {csvPreview.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', marginTop: 0 }}>
                  Preview ({csvPreview.length} rows)
                </h3>
                <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead style={{ backgroundColor: '#f9fafb' }}>
                      <tr>
                        {Object.keys(csvPreview[0]).map((key) => (
                          <th key={key} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '500', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreview.slice(0, 10).map((row, i) => (
                        <tr key={i} style={{ borderTop: i > 0 ? '1px solid #f3f4f6' : 'none' }}>
                          {Object.values(row).map((val, j) => (
                            <td key={j} style={{ padding: '8px 12px', color: '#374151' }}>
                              {val || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {csvPreview.length > 10 && (
                    <div style={{ padding: '8px 12px', textAlign: 'center', color: '#6b7280', fontSize: '12px', borderTop: '1px solid #e5e7eb' }}>
                      ...and {csvPreview.length - 10} more rows
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleImport}
                disabled={importing || csvPreview.length === 0}
                style={{
                  flex: 1,
                  padding: '10px 20px',
                  backgroundColor: (importing || csvPreview.length === 0) ? '#93c5fd' : '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (importing || csvPreview.length === 0) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => { if (!importing && csvPreview.length > 0) e.currentTarget.style.backgroundColor = '#1d4ed8'; }}
                onMouseLeave={(e) => { if (!importing && csvPreview.length > 0) e.currentTarget.style.backgroundColor = '#2563eb'; }}
              >
                <Upload style={{ width: '16px', height: '16px' }} />
                {importing ? 'Importing...' : `Import ${csvPreview.length} Contact${csvPreview.length !== 1 ? 's' : ''}`}
              </button>
              <button
                onClick={() => { setShowImportModal(false); setCsvText(''); setCsvPreview([]); }}
                style={{
                  flex: 1,
                  padding: '10px 20px',
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d1d5db'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactsPage;
